import { memo, useEffect, useRef, useState } from 'react';
import { DeviceState, WEAPON_MAX_LEVEL } from '../../game/constants';
import type { RuntimeUISnapshot } from '../../game/runtime/RuntimeUIStore';
import { ComboHud } from './ComboHud';

export function formatScore(value: number): string {
  return Math.max(0, value | 0).toString().padStart(8, '0');
}

export function formatDirection(index: number): string {
  const labels = ['FORWARD', 'DOWN-FORWARD', 'DOWN', 'DOWN-REAR', 'REAR', 'UP-REAR', 'UP', 'UP-FORWARD'];
  return labels[((index % labels.length) + labels.length) % labels.length] ?? 'FORWARD';
}

const WEAPON_LEVEL_COUNT = WEAPON_MAX_LEVEL + 1;
const POWER_ROMAN = ['Ⅰ', 'Ⅱ', 'Ⅲ', 'Ⅳ', 'Ⅴ'];
const TICKS_PER_SECOND = 60;
// CSS 側の --power-banner-dur と一致させること（要素の除去とアニメ終了を揃える）
const POWER_BANNER_MS = 1500;

interface PowerBanner {
  readonly id: number;
  readonly label: string;
}

/** React-side prev comparison, same discipline as ComboHud's chain-tier popup. */
function usePowerBanner(weaponLevel: number): PowerBanner | null {
  const previous = useRef(weaponLevel);
  const bannerId = useRef(0);
  const bannerTimer = useRef(0);
  const [banner, setBanner] = useState<PowerBanner | null>(null);

  useEffect(() => () => window.clearTimeout(bannerTimer.current), []);

  useEffect(() => {
    const prev = previous.current;
    previous.current = weaponLevel;
    if (weaponLevel <= prev) return;
    const label = POWER_ROMAN[weaponLevel];
    if (label === undefined) return;
    bannerId.current += 1;
    setBanner({ id: bannerId.current, label });
    window.clearTimeout(bannerTimer.current);
    bannerTimer.current = window.setTimeout(() => setBanner(null), POWER_BANNER_MS);
  }, [weaponLevel]);

  return banner;
}

interface HudProps {
  snapshot: RuntimeUISnapshot;
  onPause: () => void;
}

/**
 * Fix 3(b): memoized so a React commit triggered by unrelated App state
 * (settingsOpen, difficulty, isCoarsePointer...) can skip re-rendering the HUD
 * when its own props (snapshot, onPause) are referentially unchanged.
 */
export const Hud = memo(function Hud({ snapshot, onPause }: HudProps) {
  const cooldown = Math.max(0, Math.ceil(snapshot.deviceCooldown / 60));
  const powerBanner = usePowerBanner(snapshot.weaponLevel);
  const progressionReadout = snapshot.continuousArsenal
    ? `${snapshot.syncTotal.toString().padStart(3, '0')}/${snapshot.syncTarget}`
    : snapshot.lux.toString().padStart(4, '0');
  return (
    <div className="hud" aria-label="Game status">
      <div className="hud-top">
        <div className="hud-score"><span>SCORE</span><strong>{formatScore(snapshot.score)}</strong></div>
        <div className="hud-stage"><strong>0{snapshot.stageId + 1} / {snapshot.stageName}</strong><span>{snapshot.stageSubtitle}</span></div>
        <div className="hud-lux"><span>{snapshot.continuousArsenal ? 'SYNC BANK' : 'LUX'}</span><strong>{progressionReadout}</strong></div>
        <button className="hud-pause" type="button" aria-label="Pause game" onClick={onPause}>Ⅱ</button>
      </div>
      <div className="stage-progress" aria-label={`Stage progress ${Math.round(snapshot.stageProgress * 100)} percent`}>
        <i style={{ width: `${Math.round(snapshot.stageProgress * 100)}%` }} />
      </div>
      <div className="hud-bottom">
        <div className="lives" aria-label={`${snapshot.lives} of ${snapshot.maxLives} lives`}>
          <span>HULL</span>{Array.from({ length: snapshot.maxLives }, (_, i) => <i key={i} className={i < snapshot.lives ? 'live' : ''}>◆</i>)}
        </div>
        <div className="node-meter" aria-label="Prism Node charge">
          <span>NODE</span>
          {[snapshot.nodeCharge0, snapshot.nodeCharge1, snapshot.nodeCharge2].map((charge, index) => (
            <i key={index} className="node-charge"><b style={{ transform: `scaleX(${Math.max(0, Math.min(1, charge))})` }} /></i>
          ))}
        </div>
        <div className="power-meter" aria-label={`Weapon ${snapshot.continuousArsenal ? 'sync grade' : 'power level'} ${snapshot.weaponLevel + 1} of ${WEAPON_LEVEL_COUNT}`}>
          <span>{snapshot.continuousArsenal ? 'SYNC' : 'POWER'}</span>
          {Array.from({ length: WEAPON_LEVEL_COUNT }, (_, pip) => {
            const filled = pip <= snapshot.weaponLevel;
            const partial = pip === snapshot.weaponLevel + 1 && snapshot.powerNeeded > 0;
            const fraction = filled ? 1 : partial ? Math.max(0, Math.min(1, snapshot.powerCharge / snapshot.powerNeeded)) : 0;
            return (
              <i key={pip} className={`power-pip${filled ? ' filled' : ''}`}>
                <b style={{ transform: `scaleX(${fraction})` }} />
              </i>
            );
          })}
        </div>
        {snapshot.continuousArsenal ? (
          <div className="loadout-strip">
            <span>DESTROY → SYNC</span>
            <span>{snapshot.weaponLevel >= WEAPON_MAX_LEVEL ? 'LINK LASER ACTIVE' : `NEXT ${snapshot.powerCharge}/${snapshot.powerNeeded}`}</span>
            <span>{snapshot.deviceGlyph} {snapshot.deviceName}{snapshot.deviceState === DeviceState.Ready ? ' READY' : cooldown > 0 ? ` ${cooldown}s` : ' ACTIVE'}</span>
          </div>
        ) : (
          <div className="loadout-strip">
            <span>{snapshot.coreGlyph} {snapshot.coreName}</span>
            <span>{snapshot.lensGlyph} {snapshot.lensName}</span>
            <span>{snapshot.deviceGlyph} {snapshot.deviceName}{snapshot.deviceState === DeviceState.Ready ? ' READY' : cooldown > 0 ? ` ${cooldown}s` : ' ACTIVE'}</span>
          </div>
        )}
        <div className="formation-strip"><span>{snapshot.formation}</span><small>{formatDirection(snapshot.nodeDirection)}</small></div>
      </div>
      <ComboHud chain={snapshot.chain} timerFraction={snapshot.chainTimerFraction} />
      {powerBanner !== null && (
        <div className="power-banner" key={powerBanner.id}>{snapshot.continuousArsenal ? 'SYNC' : 'POWER'} GRADE {powerBanner.label}</div>
      )}
      {snapshot.gateAheadTicks >= 0 && snapshot.gateAheadTicks <= snapshot.gateApproachTicks && (
        <div className="gate-ahead-banner" role="status">
          <i aria-hidden="true">▸▸</i>
          <span>{snapshot.continuousArsenal ? 'SYNC SURGE' : 'CALIBRATION GATE'} AHEAD · {Math.ceil(snapshot.gateAheadTicks / TICKS_PER_SECOND)}</span>
          <i aria-hidden="true">▸▸</i>
        </div>
      )}
      {snapshot.bossActive && (
        <div className="boss-hud">
          <div><strong>{snapshot.bossName}</strong><span>{snapshot.bossPhase}</span></div>
          <i><b style={{ transform: `scaleX(${Math.max(0, Math.min(1, snapshot.bossHp))})` }} /></i>
        </div>
      )}
    </div>
  );
});
